import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import isEmpty from 'lodash-es/isEmpty'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { RENTAL_VIEWING_ENTITY } from '../../../constants/entity'
import { MENU_RENTAL_PORTAL_ITEM } from '../../../constants/menu'
import {
  ROUTE_PATH_LIST_RENTAL_PORTAL,
  ROUTE_UI_SECTION_NAME_RENTAL_PORTAL,
} from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import { usePageBrowserTabTitle, useTimeZone } from '../../../hooks'
import {
  isListViewTypeCalendar,
  isListViewTypeWithPreview,
} from '../../../utils/listViewType'
import Button from '../../Button'
import { FilterBar } from '../../Filter'
import { MultipleViewTypeListLayout } from '../../MultipleViewTypeList'
import { NotificationInline } from '../../Notification'
import { Page, PageActions } from '../../Page'
import { PageActionsButtonAddEntity } from '../../PageActionsButtonAddEntity'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { PageHeader } from '../../PageHeader'
import {
  getIsViewingSlotBookable,
  getViewingProfileLink,
  MOCK_VIEWINGS_AVAILABLE_FILTERS,
} from '../../RentalPortalViewing'
import { TableExportData } from '../../Table'
import { TablePagination } from '../../TablePagination'
import Text from '../../Text'
import {
  RENTAL_PORTAL_LIST_RESOURCE,
  useRentalPortalExport,
  useRentalPortalListPermissions,
  useRentalPortalSectionPermissions,
} from '../RentalPortal.hooks'
import { getRentalPortalSubsectionByPathname } from '../RentalPortal.utils'
import { RentalPortalActiveItemPreview } from '../RentalPortalActiveItemPreview'
import { useRentalPortalViewingApplicantPreview } from '../RentalPortalViewingApplicantPreviewModal'

import {
  QA_NAME_VIEWINGS_ADD_NEW,
  QA_NAME_VIEWINGS_CLEAR_FILTERS,
  QA_NAME_VIEWINGS_PAGE,
  QA_NAME_VIEWINGS_PREVIEW,
  QA_NAME_VIEWINGS_REFRESH_ERROR,
  QA_NAME_VIEWINGS_RETRY,
  VIEWING_CREATE_ACTION_NAME,
  VIEWINGS_FILTER_BAR_ITEMS_PROPS,
  VIEWINGS_INITIAL_RANGE_FILTER_NAMES,
} from './RentalPortalViewings.constants'
import {
  useCreateRentalPortalViewing,
  useRentalPortalViewingPreview,
  useRentalPortalViewingsArchiveUndo,
  useRentalPortalViewingsCalendar,
  useRentalPortalViewingsFilters,
  useRentalPortalViewingsMassActions,
  useRentalPortalViewingsMockTable,
  useRentalPortalViewingsMultipleViewTypeList,
  useRentalPortalViewingsViewRegime,
  useRentalPortalViewingTableActions,
} from './RentalPortalViewings.hooks'
import {
  getIsViewingActionOpen,
  getTranslatedViewingsAvailableFilters,
  getViewingsRefreshErrorText,
  getViewingTableItemActions,
  getViewingViewRegimeOptions,
} from './RentalPortalViewings.utils'
import { RentalPortalViewingsPreviewEmpty } from './RentalPortalViewingsPreviewEmpty'
import { RentalPortalViewingTableLayout } from './RentalPortalViewingTableLayout'
import { getViewingExportValue } from './RentalPortalViewingTableRow'

import * as styles from './RentalPortalViewings.module.css'

import RefreshIcon from '../../../icons/refresh.svg'
import ViewingIcon from '../../../icons/viewing.svg'

function RentalPortalViewings({ ActiveItemPreview }) {
  const { t } = useTranslation()

  const { pathname } = useLocation()

  const navigate = useNavigate()

  const [modal, setModal] = useState(null)

  const availableFilters = getTranslatedViewingsAvailableFilters({
    availableFilters: MOCK_VIEWINGS_AVAILABLE_FILTERS,
    t,
  })

  const {
    filterBarValues,
    filterValues,
    handleClearFilters,
    handleSetFilterValues,
  } = useRentalPortalViewingsFilters({ availableFilters })

  const { handleChangeViewType, listViewType } =
    useRentalPortalViewingsViewRegime()

  const isCalendarRegime = isListViewTypeCalendar(listViewType)

  const hasPreview = isListViewTypeWithPreview(listViewType)

  const tablePage = useRentalPortalViewingsMockTable({ filterValues })

  const calendar = useRentalPortalViewingsCalendar({
    filterValues,
    isEnabled: isCalendarRegime,
  })

  const {
    allItems,
    checkIsItemSelected,
    excludeIds,
    handleCleanSelection,
    handleRefresh,
    handleSetPagination,
    handleSetPreview,
    handleUpdateConfig,
    isLoading,
    isRefetching,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    preview,
    selectAllPages,
    selected,
    serverError,
  } = tablePage

  const hasViewings = !isEmpty(items?.items)

  async function handleReloadList() {
    calendar.handleReload()

    return handleRefresh()
  }

  const {
    activeItem,
    handleChangeFocusedRow,
    handleClickRow,
    handleMouseDownRow,
    handlePreviewResizeEnd,
    handleResetViewTypeToPlainBody,
    isSplitAvailable,
  } = useRentalPortalViewingsMultipleViewTypeList({
    handleReload: handleReloadList,
    handleSetPreview,
    handleSetView: handleChangeViewType,
    handleUpdateConfig,
    items: items?.items,
    listViewType,
    preview,
  })

  const { handleAddArchiveUndoNotification } =
    useRentalPortalViewingsArchiveUndo({
      onReload: handleReloadList,
    })

  const { handleOpenBooking, handleOpenCreate } = useCreateRentalPortalViewing({
    setModal,
    onClose: handleCloseModal,
    onReload: handleReloadList,
  })

  function handleClickCreate() {
    handleOpenCreate()
  }

  const { handleOpenApplicantPreview } = useRentalPortalViewingApplicantPreview(
    {
      setModal,
      onClose: handleCloseModal,
    }
  )

  const { handleOpenViewingPreview } = useRentalPortalViewingPreview({
    setModal,
    onClose: handleCloseModal,
    onEntityChange: handleReloadList,
  })

  const { massAction, massActions, setMassAction } =
    useRentalPortalViewingsMassActions({
      allItems,
      excludeIds,
      filterValues,
      selectAllPages,
      selected,
      setModal,
      onCleanSelection: handleCleanSelection,
      onClose: handleCloseModal,
      onReload: handleReloadList,
      onUndo: handleAddArchiveUndoNotification,
    })

  function handleCallRowAction(action, item) {
    if (getIsViewingActionOpen(action)) {
      navigate(getViewingProfileLink(item.id))
    } else {
      setItemAction(action, item)
    }
  }

  const { setItemAction } = useRentalPortalViewingTableActions({
    setModal,
    onBook: handleOpenBooking,
    onClose: handleCloseModal,
    onPerformAction: massAction,
    onReload: handleReloadList,
  })

  const { canCreate } = useRentalPortalSectionPermissions({
    sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.viewings,
  })

  const { listPermissions } = useRentalPortalListPermissions({
    resource: RENTAL_PORTAL_LIST_RESOURCE.viewings,
  })

  const { handleExport, isExportAvailable } = useRentalPortalExport({
    checkIsItemSelected,
    columns: tablePage.columns,
    getValue: getViewingExportValue,
    items: allItems,
    section: ROUTE_PATH_LIST_RENTAL_PORTAL.viewings,
    selectAllPages,
    selected,
  })

  const subsection = getRentalPortalSubsectionByPathname(pathname)

  const timezone = useTimeZone()

  usePageBrowserTabTitle({ isLoading, pageTitle: t(subsection.title) })

  const hasBodyError = serverError && !hasViewings

  const hasStaleError = serverError && hasViewings

  const hasActiveFilters = !isEmpty(filterValues)

  const shouldShowFilterBar =
    hasViewings || hasActiveFilters || isLoading || hasBodyError

  const breadcrumbs = [
    {
      id: MENU_RENTAL_PORTAL_ITEM.title,
      title: t(MENU_RENTAL_PORTAL_ITEM.title),
      to: `/${ROUTE_PATH_LIST_RENTAL_PORTAL.root}`,
    },
    { id: subsection.title, title: t(subsection.title) },
  ]

  function handleCloseModal() {
    setModal(null)
  }

  function handleClickCalendarViewing(viewing, event) {
    if (hasPreview) {
      handleClickRow(viewing.id, event)
    } else if (getIsViewingSlotBookable({ timezone, viewing })) {
      handleOpenBooking(viewing)
    } else {
      handleOpenViewingPreview(viewing.id)
    }
  }

  const emptyStateActions = getEmptyStateActions({
    actionName: VIEWING_CREATE_ACTION_NAME,
    isLoading,
    qaName: QA_NAME_VIEWINGS_ADD_NEW,
    shouldShowActions: canCreate,
    text: t('RentalPortal.ViewingAddNewBtnKey'),
    onClick: handleClickCreate,
  })

  return (
    <MultipleViewTypeListLayout
      isEmptyItems={!hasViewings}
      listViewType={listViewType}
      pageScrollWrapperRef={pageScrollWrapperRef}
      previewProps={{
        Component: RentalPortalActiveItemPreview,
        Empty: RentalPortalViewingsPreviewEmpty,
        activeItem,
        canResize: true,
        componentProps: {
          Component: ActiveItemPreview,
          onClose: handleResetViewTypeToPlainBody,
          onEntityChange: handleReloadList,
        },
        emptyProps: {
          EntityIcon: ViewingIcon,
          text: t('RentalPortal.ViewingPreviewEmptyStateKey'),
          onClosePreview: handleResetViewTypeToPlainBody,
        },
        qaName: QA_NAME_VIEWINGS_PREVIEW,
        resizeConfig: {
          initialWidth: preview?.size,
          onResizeEnd: handlePreviewResizeEnd,
        },
      }}
    >
      <ScreenContainerRef value={pageScrollWrapperRef}>
        <Page
          pagination={
            isCalendarRegime ? null : (
              <TablePagination
                responsive
                isSkeleton={isLoading}
                value={pagination}
                onChange={handleSetPagination}
              />
            )
          }
          qaName={QA_NAME_VIEWINGS_PAGE}
          scrollWrapperRef={pageScrollWrapperRef}
        >
          <PageHeader
            breadcrumbs={breadcrumbs}
            isLoading={isLoading}
            itemsCount={pagination.count}
            title={t(subsection.title)}
          >
            <PageActions className={styles.pageActions}>
              {permissions.can_export || isLoading ? (
                <TableExportData
                  buttonProps={{
                    disabledTooltip: 'RentalPortal.ExportEmptyTooltipKey',
                  }}
                  containerRef={pageScrollWrapperRef}
                  disabled={!isExportAvailable}
                  isSkeleton={isLoading}
                  titleKey='RentalPortal.ViewingExportBtnKey'
                  onClick={handleExport}
                />
              ) : null}
              {canCreate ? (
                <PageActionsButtonAddEntity
                  isSkeleton={isLoading}
                  qaName={QA_NAME_VIEWINGS_ADD_NEW}
                  text={t('RentalPortal.ViewingAddNewBtnKey')}
                  onClick={handleClickCreate}
                />
              ) : null}
            </PageActions>
          </PageHeader>
          {shouldShowFilterBar ? (
            <FilterBar
              hideFilterTemplateSkeleton
              responsive
              availableFilters={availableFilters}
              buttonsClassName={styles.filterBarButtons}
              canViewFilterTemplate={listPermissions.can_view_filter_template}
              className={styles.filterBar}
              entity={RENTAL_VIEWING_ENTITY}
              filterBarItemsProps={{
                ...VIEWINGS_FILTER_BAR_ITEMS_PROPS,
                className: styles.filterBarItems,
                controlClassName: styles.filterBarControl,
              }}
              filterValues={filterBarValues}
              initialRangeFilterNames={VIEWINGS_INITIAL_RANGE_FILTER_NAMES}
              isSkeleton={isLoading}
              ordering={ordering}
              pagination={{ per_page: pagination.pageSize }}
              setFilterValues={handleSetFilterValues}
              onReload={handleReloadList}
            />
          ) : null}
          {hasStaleError ? (
            <NotificationInline
              actions={[
                {
                  beforeIconProps: { Icon: RefreshIcon },
                  callback: handleReloadList,
                  isLoading: isRefetching,
                  text: t('RentalPortal.ListingRetryBtnKey'),
                },
              ]}
              className={styles.refreshError}
              qaName={QA_NAME_VIEWINGS_REFRESH_ERROR}
              text={getViewingsRefreshErrorText({
                availableFilters,
                filterValues,
                t,
              })}
              type={NOTIFICATION_TYPE.error}
            />
          ) : null}
          <RentalPortalViewingTableLayout
            activeItem={activeItem}
            calendar={calendar}
            changeViewTypeButtonProps={{
              hasSplit: isSplitAvailable,
              listViewType,
              options: getViewingViewRegimeOptions({
                isSplitAvailable,
                listViewType,
              }),
              onChange: handleChangeViewType,
            }}
            emptyState={
              <PageEmptyState
                EntityIcon={subsection.EmptyStateIcon}
                actions={emptyStateActions}
                text={hasActiveFilters ? undefined : t(subsection.emptyTextKey)}
              >
                {hasActiveFilters ? (
                  <>
                    <Text isSecondary>
                      {t('RentalPortal.ViewingsNoFilterResultsKey')}
                    </Text>
                    <Button.Flat
                      qaName={QA_NAME_VIEWINGS_CLEAR_FILTERS}
                      text={t('Table.ClearFilterBtnKey')}
                      onClick={handleClearFilters}
                    />
                  </>
                ) : null}
              </PageEmptyState>
            }
            errorState={
              <PageEmptyState EntityIcon={subsection.EmptyStateIcon}>
                <Text isSecondary>
                  {t('RentalPortal.ViewingsRefreshErrorKey')}
                </Text>
                <Button.Flat
                  beforeIconProps={{ Icon: RefreshIcon }}
                  isLoading={isRefetching}
                  qaName={QA_NAME_VIEWINGS_RETRY}
                  text={t('RentalPortal.ListingRetryBtnKey')}
                  onClick={handleReloadList}
                />
              </PageEmptyState>
            }
            getViewingItemActions={getViewingTableItemActions}
            hasBodyError={hasBodyError}
            hasTable={
              isCalendarRegime || hasViewings || isLoading || hasBodyError
            }
            isCalendarRegime={isCalendarRegime}
            massActions={massActions}
            modal={modal}
            setMassAction={setMassAction}
            tablePage={tablePage}
            onCallItemAction={handleCallRowAction}
            onChangeFocusedRow={handleChangeFocusedRow}
            onClickApplicant={handleOpenApplicantPreview}
            onClickCalendarViewing={handleClickCalendarViewing}
            onClickEmptyCalendarCell={handleOpenCreate}
            onClickRow={handleClickRow}
            onMouseDownRow={handleMouseDownRow}
          />
        </Page>
      </ScreenContainerRef>
    </MultipleViewTypeListLayout>
  )
}

export default RentalPortalViewings
